// components/UserManagement.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import API from '../services/api';
import '../styles/UserManagement.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faUserPlus, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    role: 'user',
    allowedStoneIds: [],
    allowedAttributes: [],
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isGuestConfigLoading, setIsGuestConfigLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allStones, setAllStones] = useState([]);
  const [allPossibleAttributes, setAllPossibleAttributes] = useState([]);
  const [stoneNameFilter, setStoneNameFilter] = useState('');
  const [kategorieOptions, setKategorieOptions] = useState([]);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [farbenOptions, setFarbenOptions] = useState([]);
  const [selectedFarbeFilter, setSelectedFarbeFilter] = useState('');
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [serienBezeichnungOptions, setSerienBezeichnungOptions] = useState([]);
  const [selectedSerienBezeichnungFilter, setSelectedSerienBezeichnungFilter] = useState('');
  const [selectedIstSerieFilter, setSelectedIstSerieFilter] = useState(''); // '', 'true', 'false'

  const attributeDisplayNames = {
    name: 'Name',
    bildPfad: 'Bild Vorschau',
    kategorie: 'Kategorie',
    farbe: 'Farbe',
    status: 'Status',
    gewicht: 'Gewicht (ct)',
    preisProGramm: 'Preis pro Gramm (€)',
    durchmesser: 'Durchmesser (mm)',
    laenge: 'Länge (mm)',
    breite: 'Breite (mm)',
    serienBezeichnung: 'Serienbezeichnung',
    kaufPreis: 'Kaufpreis (€)',
    kaufDatum: 'Kaufdatum',
    kaufOrt: 'Kaufort',
    verkaeufer: 'Verkäufer',
    verkaufPreis: 'Verkaufspreis (€)',
    verkaufDatum: 'Verkaufsdatum',
    verkaufOrt: 'Verkaufsort',
    kaeufer: 'Käufer',
    notizen: 'Notizen',
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: 'user',
      allowedStoneIds: [],
      allowedAttributes: [],
    });
    setEditingUser(null);
    setStoneNameFilter('');
    setSelectedCategoryFilter('');
    setSelectedFarbeFilter('');
    setSelectedStatusFilter('');
    setSelectedSerienBezeichnungFilter('');
    setSelectedIstSerieFilter('');
  };

  const fetchUsers = useCallback(() => {
    setIsLoading(true);
    setError(null);
    API.get('/users')
      .then((res) => {
        setUsers(res.data);
      })
      .catch((err) => {
        console.error("Fehler beim Laden der Benutzer:", err);
        setError('Fehler beim Laden der Benutzer.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const fetchGuestPermissionData = useCallback(() => {
    setIsGuestConfigLoading(true);
    setError(null);
    Promise.all([
      API.get('/steine/all-basic'),
      API.get('/steine/attributes'),
      API.get('/steine/kategorien'),
      API.get('/steine/farben'),
      API.get('/steine/status-values'),
      API.get('/steine/serienBezeichnungen')
    ])
    .then(([stonesRes, attributesRes, kategorienRes, farbenRes, statusRes, serienRes]) => {
      setAllStones(stonesRes.data || []);
      setAllPossibleAttributes(attributesRes.data || []);
      setKategorieOptions(['Alle Kategorien', ...(kategorienRes.data?.filter(Boolean).sort() || [])]);
      setFarbenOptions(['Alle Farben', ...(farbenRes.data?.filter(Boolean).sort() || [])]);
      setStatusOptions(['Alle Status', ...(statusRes.data?.sort() || [])]);
      setSerienBezeichnungOptions(['Alle Serien', ...(serienRes.data?.filter(Boolean).sort() || [])]);
    })
    .catch(err => {
      console.error("Fehler beim Laden der Daten für Gastberechtigungen:", err);
      setError('Fehler beim Laden der Konfigurationsdaten für Gast-Benutzer.');
      setAllStones([]);
      setAllPossibleAttributes([]);
    })
    .finally(() => {
      setIsGuestConfigLoading(false);
    });
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchGuestPermissionData();
  }, [fetchUsers, fetchGuestPermissionData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      if (name === 'allowedStoneIds') {
        const stoneId = value;
        setFormData(prevData => ({
          ...prevData,
          allowedStoneIds: checked
            ? [...prevData.allowedStoneIds, stoneId]
            : prevData.allowedStoneIds.filter(id => id !== stoneId),
        }));
      } else if (name === 'allowedAttributes') {
        const attributeName = value;
        setFormData(prevData => ({
          ...prevData,
          allowedAttributes: checked
            ? [...prevData.allowedAttributes, attributeName]
            : prevData.allowedAttributes.filter(attr => attr !== attributeName),
        }));
      } else {
        setFormData(prevData => ({
          ...prevData,
          [name]: checked,
        }));
      }
    } else if (name === "role") {
        if (value !== 'guest' && formData.role === 'guest') {
            setFormData(prevData => ({
                ...prevData,
                role: value,
                allowedStoneIds: [],
                allowedAttributes: [],
            }));
        } else {
            setFormData(prevData => ({
                ...prevData,
                role: value,
            }));
        }
    }
    else {
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    }
  };

  const handleAddUser = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const userData = { ...formData };
    if (formData.role !== 'guest') {
      delete userData.allowedStoneIds;
      delete userData.allowedAttributes;
    }

    API.post('/users', userData)
      .then((res) => {
        fetchUsers();
        resetForm();
      })
      .catch((err) => {
        console.error("Fehler beim Hinzufügen des Benutzers:", err);
        setError(err.response?.data?.message || 'Fehler beim Hinzufügen des Benutzers.');
        alert(err.response?.data?.message || 'Fehler beim Hinzufügen des Benutzers.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      password: '',
      role: user.role,
      allowedStoneIds: user.role === 'guest' ? user.allowedStoneIds || [] : [],
      allowedAttributes: user.role === 'guest' ? user.allowedAttributes || [] : [],
    });
  };

  const handleUpdateUser = (e) => {
    e.preventDefault();
    if (!editingUser) return;

    setIsLoading(true);
    setError(null);

    const updateData = {
        username: formData.username,
        role: formData.role,
    };
    if (formData.password) {
        updateData.password = formData.password;
    }

    if (formData.role === 'guest') {
      updateData.allowedStoneIds = formData.allowedStoneIds;
      updateData.allowedAttributes = formData.allowedAttributes;
    }

    API.put(`/users/${editingUser._id}`, updateData)
      .then(() => {
        fetchUsers();
        resetForm();
      })
      .catch((err) => {
        console.error("Fehler beim Aktualisieren des Benutzers:", err);
        setError(err.response?.data?.message || 'Fehler beim Aktualisieren des Benutzers.');
        alert(err.response?.data?.message || 'Fehler beim Aktualisieren des Benutzers.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const handleDeleteUser = (id) => {
    if (window.confirm('Benutzer wirklich löschen?')) {
      setIsLoading(true);
      setError(null);
      API.delete(`/users/${id}`)
        .then(() => {
          fetchUsers();
        })
        .catch((err) => {
          console.error("Fehler beim Löschen des Benutzers:", err);
          setError(err.response?.data?.message || 'Fehler beim Löschen des Benutzers.');
          alert(err.response?.data?.message || 'Fehler beim Löschen des Benutzers.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleStoneNameFilterChange = (e) => {
    setStoneNameFilter(e.target.value);
  };

  const handleCategoryFilterChange = (e) => {
    setSelectedCategoryFilter(e.target.value === 'Alle Kategorien' ? '' : e.target.value);
  };

  const handleFarbeFilterChange = (e) => {
    setSelectedFarbeFilter(e.target.value === 'Alle Farben' ? '' : e.target.value);
  };

  const handleStatusFilterChange = (e) => {
    setSelectedStatusFilter(e.target.value === 'Alle Status' ? '' : e.target.value);
  };

  const handleSerienBezeichnungFilterChange = (e) => {
    setSelectedSerienBezeichnungFilter(e.target.value === 'Alle Serien' ? '' : e.target.value);
  };

  const handleIstSerieFilterChange = (e) => {
    setSelectedIstSerieFilter(e.target.value); // '', 'true', 'false'
  };

  const filteredStones = useMemo(() => {
    let stones = [...allStones];
    if (selectedCategoryFilter) {
      stones = stones.filter(stone => stone.kategorie === selectedCategoryFilter);
    }
    if (selectedFarbeFilter) {
      stones = stones.filter(stone => stone.farbe === selectedFarbeFilter);
    }
    if (selectedStatusFilter) {
      stones = stones.filter(stone => stone.status === selectedStatusFilter);
    }
    if (selectedSerienBezeichnungFilter) {
      stones = stones.filter(stone => stone.serienBezeichnung === selectedSerienBezeichnungFilter);
    }
    if (selectedIstSerieFilter) {
      // Konvertiere den String-Wert des Filters in einen Boolean für den Vergleich
      const istSerieBoolean = selectedIstSerieFilter === 'true';
      stones = stones.filter(stone => stone.istSerie === istSerieBoolean);
    }
    if (stoneNameFilter) {
      stones = stones.filter(stone => 
        stone.name.toLowerCase().includes(stoneNameFilter.toLowerCase())
      );
    }
    return stones;
  }, [allStones, stoneNameFilter, selectedCategoryFilter, selectedFarbeFilter, selectedStatusFilter, selectedSerienBezeichnungFilter, selectedIstSerieFilter]);

  // Handler zum Auswählen aller aktuell sichtbaren (gefilterten) Steine
  const handleSelectAllVisible = () => {
    const visibleStoneIds = filteredStones.map(stone => stone._id);
    setFormData(prevData => ({
      ...prevData,
      allowedStoneIds: [...new Set([...prevData.allowedStoneIds, ...visibleStoneIds])]
    }));
  };

  // Handler zum Abwählen aller aktuell sichtbaren (gefilterten) Steine
  const handleDeselectAllVisible = () => {
    const visibleStoneIdsSet = new Set(filteredStones.map(stone => stone._id));
    setFormData(prevData => ({
      ...prevData,
      allowedStoneIds: prevData.allowedStoneIds.filter(id => !visibleStoneIdsSet.has(id))
    }));
  };

  return (
    <main className="user-management">
      <h2>Benutzerverwaltung</h2>

      <section className="role-rights">
        <h3>Rechte der Benutzerrollen</h3>
        <div className="role-rights-grid">
          <div className="role-card admin">
            <h4>Admin</h4>
            <ul>
              <li>Kann Benutzer erstellen (einschließlich Gast-Benutzer)</li>
              <li>Hat Zugriff auf den Adminbereich</li>
              <li>Kann Steine erstellen, bearbeiten und löschen</li>
            </ul>
          </div>
          <div className="role-card user">
            <h4>User</h4>
            <ul>
              <li>Kann sich anmelden und die Liste der Steine ansehen</li>
              <li>Kann Steindetails ansehen, aber keine Änderungen vornehmen</li>
            </ul>
          </div>
          <div className="role-card guest">
            <h4>Gast</h4>
            <ul>
              <li>Kann sich anmelden und eine vom Admin definierte Auswahl an Steinen sehen.</li>
              <li>Sieht nur die vom Admin freigegebenen Attribute der Steine.</li>
              <li>Kann keine Änderungen vornehmen oder neue Steine erstellen.</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="user-form-section">
        <h3>{editingUser ? 'Benutzer bearbeiten' : 'Neuen Benutzer hinzufügen'}</h3>
        <form onSubmit={editingUser ? handleUpdateUser : handleAddUser} className="user-form">
          <div className="form-group">
            <label htmlFor="username">Benutzername:</label>
            <input
              type="text"
              id="username"
              name="username"
              placeholder="Benutzername"
              value={formData.username}
              onChange={handleChange}
              required
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="password">{editingUser ? 'Neues Passwort (optional):' : 'Passwort:'}</label>
            <input
              type="password"
              id="password"
              name="password"
              placeholder={editingUser ? 'Leer lassen, um nicht zu ändern' : 'Passwort'}
              value={formData.password}
              onChange={handleChange}
              required={!editingUser}
              disabled={isLoading}
            />
          </div>
          <div className="form-group">
            <label htmlFor="role">Rolle:</label>
            <select id="role" name="role" value={formData.role} onChange={handleChange} disabled={isLoading}>
              <option value="user">Benutzer</option>
              <option value="admin">Admin</option>
              <option value="guest">Gast</option>
            </select>
          </div>

          {formData.role === 'guest' && (
            <>
              <div className="form-group guest-permissions-section">
                <label className="guest-permissions-main-label">Berechtigungen für Gast-Benutzer:</label>
                
                <div className="stones-filter-and-selection-container">
                  <div className="stones-filters-column">
                    <h4>Steine filtern</h4>
                    <div className="form-group">
                      <label htmlFor="stone-category-filter">Nach Kategorie:</label>
                      <select 
                        id="stone-category-filter"
                        className="form-input"
                        value={selectedCategoryFilter || 'Alle Kategorien'}
                        onChange={handleCategoryFilterChange}
                        disabled={isLoading || kategorieOptions.length <= 1}
                      >
                        {kategorieOptions.map(kat => <option key={kat} value={kat}>{kat}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="stone-farbe-filter">Nach Farbe:</label>
                      <select 
                        id="stone-farbe-filter"
                        className="form-input"
                        value={selectedFarbeFilter || 'Alle Farben'}
                        onChange={handleFarbeFilterChange}
                        disabled={isLoading || farbenOptions.length <= 1}
                      >
                        {farbenOptions.map(farbe => <option key={farbe} value={farbe}>{farbe}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="stone-status-filter">Nach Status:</label>
                      <select 
                        id="stone-status-filter"
                        className="form-input"
                        value={selectedStatusFilter || 'Alle Status'}
                        onChange={handleStatusFilterChange}
                        disabled={isLoading || statusOptions.length <= 1}
                      >
                        {statusOptions.map(status => <option key={status} value={status}>{status}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="stone-serienBezeichnung-filter">Nach Serienbezeichnung:</label>
                      <select 
                        id="stone-serienBezeichnung-filter"
                        className="form-input"
                        value={selectedSerienBezeichnungFilter || 'Alle Serien'}
                        onChange={handleSerienBezeichnungFilterChange}
                        disabled={isLoading || serienBezeichnungOptions.length <= 1}
                      >
                        {serienBezeichnungOptions.map(serie => <option key={serie} value={serie}>{serie}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="stone-istSerie-filter">Ist Serie:</label>
                      <select 
                        id="stone-istSerie-filter"
                        className="form-input"
                        value={selectedIstSerieFilter}
                        onChange={handleIstSerieFilterChange}
                        disabled={isLoading}
                      >
                        <option value="">Alle</option>
                        <option value="true">Ja</option>
                        <option value="false">Nein</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label htmlFor="stone-name-filter">Nach Name:</label>
                      <input 
                        id="stone-name-filter"
                        type="text"
                        placeholder="Namen eingeben..."
                        className="form-input stone-filter-input"
                        value={stoneNameFilter}
                        onChange={handleStoneNameFilterChange}
                        disabled={isLoading || allStones.length === 0}
                      />
                    </div>
                  </div>

                  <div className="stones-selection-column">
                    <div className="stones-selection-header">
                      <h4>Steine auswählen ({filteredStones.length} / {allStones.length})</h4>
                      {filteredStones.length > 0 && (
                        <div className="select-all-buttons">
                          <button type="button" onClick={handleSelectAllVisible} className="btn btn-small btn-link">
                            Alle Sichtbaren auswählen
                          </button>
                          <button type="button" onClick={handleDeselectAllVisible} className="btn btn-small btn-link">
                            Auswahl Sichtbarer aufheben
                          </button>
                        </div>
                      )}
                    </div>
                    {allStones.length > 0 ? (
                      <div className="checkbox-group scrollable-checkbox-group stones-selection-list">
                        {filteredStones.length > 0 ? (
                            filteredStones.map(stone => (
                            <div key={stone._id} className="checkbox-item stone-selection-item">
                              {stone.bildPfad && (
                                <img 
                                  src={`${process.env.REACT_APP_BASE_URL}/${stone.bildPfad}`}
                                  alt={stone.name}
                                  className="stone-preview-image"
                                  onError={(e) => { e.target.style.display = 'none';}}
                                />
                              )}
                              {!stone.bildPfad && <div className="stone-preview-placeholder"></div>}
                              <input
                                type="checkbox"
                                id={`stone-${stone._id}`}
                                name="allowedStoneIds"
                                value={stone._id}
                                checked={formData.allowedStoneIds.includes(stone._id)}
                                onChange={handleChange}
                                disabled={isLoading}
                              />
                              <label htmlFor={`stone-${stone._id}`}>{stone.name} {stone.kategorie && `(${stone.kategorie})`}</label>
                            </div>
                          )))
                          : (<p>Keine Steine entsprechen den aktuellen Filtern.</p>)
                        }
                      </div>
                    ) : (
                      <p>Lade Steine oder keine Steine vorhanden...</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Erlaubte Attribute für Steine:</label>
                {isGuestConfigLoading ? (
                  <p>Lade Attribute für Auswahl...</p>
                ) : allPossibleAttributes.length > 0 ? (
                  <div className="checkbox-group scrollable-checkbox-group attribute-checkbox-group">
                    {allPossibleAttributes.map(attr => (
                      <div key={attr} className="checkbox-item">
                        <input
                          type="checkbox"
                          id={`attr-${attr}`}
                          name="allowedAttributes"
                          value={attr}
                          checked={formData.allowedAttributes.includes(attr)}
                          onChange={handleChange}
                          disabled={isLoading || isGuestConfigLoading}
                        />
                        <label htmlFor={`attr-${attr}`}>{attributeDisplayNames[attr] || attr}</label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p>Keine Attribute für Auswahl vorhanden oder Fehler beim Laden.</p>
                )}
              </div>
            </>
          )}

          {error && <p className="error-message">{error}</p>}
          <div className="button-group">
            {editingUser ? (
              <>
                <button type="submit" className="btn btn-submit" disabled={isLoading}>
                  <FontAwesomeIcon icon={faSave} /> {isLoading ? 'Speichern...' : 'Aktualisieren'}
                </button>
                <button type="button" className="btn btn-cancel" onClick={handleCancelEdit} disabled={isLoading}>
                  <FontAwesomeIcon icon={faTimes} /> Abbrechen
                </button>
              </>
            ) : (
              <button type="submit" className="btn btn-submit" disabled={isLoading}>
                <FontAwesomeIcon icon={faUserPlus} /> {isLoading ? 'Hinzufügen...' : 'Benutzer hinzufügen'}
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="user-list-section">
        <h3>Bestehende Benutzer</h3>
        {isLoading && users.length === 0 && <p>Lade Benutzer...</p>}
        {!isLoading && users.length === 0 && <p>Keine Benutzer gefunden.</p>}
        {users.length > 0 && (
          <table className="user-table">
            <thead>
              <tr>
                <th>Benutzername</th>
                <th>Rolle</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>{user.username}</td>
                  <td>{user.role}</td>
                  <td>
                    <button
                      onClick={() => handleEditUser(user)}
                      className="btn-icon"
                      title="Benutzer bearbeiten"
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user._id)}
                      className="btn-icon"
                      title="Benutzer löschen"
                      disabled={isLoading}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

export default UserManagement;